import React, { useEffect, useState, useCallback } from 'react';
import { RequireRole } from '../../components/CallcenterV2/RequireRole';
import {
  fetchPauseReasons, createPauseReason, updatePauseReason, deletePauseReason,
} from '../../services/callcenterV2/pauseReasonsService';
import '../../components/CallcenterV2/ccv2-components.css';
import './config-styles.css';

const EMPTY_FORM = { name: '', color: '#4A7BA7', isGlobal: true, isActive: true };

function PauseReasonModal({ reason, onClose, onSave, saving }) {
  const [form, setForm] = useState(reason ? { ...reason, isGlobal: reason.is_global !== false, isActive: reason.is_active !== false } : EMPTY_FORM);
  const isEdit = !!reason;

  const handle = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  return (
    <div className="ccv2-p5-modal-overlay" onClick={onClose}>
      <div className="ccv2-p5-modal" onClick={(e) => e.stopPropagation()}>
        <div className="ccv2-p5-modal-header">
          <h3 className="ccv2-p5-modal-title">{isEdit ? 'Editar Motivo de Pausa' : 'Novo Motivo de Pausa'}</h3>
          <button className="ccv2-p5-modal-close" onClick={onClose}>×</button>
        </div>
        <div className="ccv2-p5-modal-body">
          <div className="ccv2-p5-field">
            <label className="ccv2-p5-label">Nome *</label>
            <input className="ccv2-p5-input" value={form.name} onChange={(e) => handle('name', e.target.value)} style={{ width: '100%' }} placeholder="Ex: Almoço, Reunião..." />
          </div>
          <div className="ccv2-p5-field">
            <label className="ccv2-p5-label">Cor</label>
            <input type="color" value={form.color || '#4A7BA7'} onChange={(e) => handle('color', e.target.value)} style={{ width: 48, height: 36, cursor: 'pointer', border: 'none', background: 'none' }} />
          </div>
          <div className="ccv2-p5-field" style={{ display: 'flex', gap: '1.5rem' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
              <input type="checkbox" checked={form.isGlobal} onChange={(e) => handle('isGlobal', e.target.checked)} />
              <span className="ccv2-p5-label" style={{ margin: 0 }}>Global (todos os agentes)</span>
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
              <input type="checkbox" checked={form.isActive} onChange={(e) => handle('isActive', e.target.checked)} />
              <span className="ccv2-p5-label" style={{ margin: 0 }}>Ativo</span>
            </label>
          </div>
        </div>
        <div className="ccv2-p5-modal-footer">
          <button className="ccv2-p5-btn ccv2-p5-btn-secondary" onClick={onClose}>Cancelar</button>
          <button className="ccv2-p5-btn ccv2-p5-btn-primary" disabled={!form.name.trim() || saving} onClick={() => onSave(form, isEdit)}>
            {saving ? '⌛' : '💾'} {isEdit ? 'Salvar' : 'Criar'}
          </button>
        </div>
      </div>
    </div>
  );
}

function PauseReasons() {
  const [reasons, setReasons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [editReason, setEditReason] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [includeInactive, setIncludeInactive] = useState(false);

  const loadReasons = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await fetchPauseReasons(includeInactive);
      setReasons(Array.isArray(data) ? data : []);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [includeInactive]);

  useEffect(() => { loadReasons(); }, [loadReasons]);

  const handleSave = useCallback(async (form, isEdit) => {
    setSaving(true);
    try {
      const dto = { name: form.name, color: form.color, isGlobal: form.isGlobal, isActive: form.isActive };
      if (isEdit) {
        await updatePauseReason(editReason.id_pause_reason, dto);
        setSuccess('Motivo de pausa atualizado com sucesso.');
      } else {
        await createPauseReason(dto);
        setSuccess('Motivo de pausa criado com sucesso.');
      }
      setShowModal(false);
      setEditReason(null);
      loadReasons();
      setTimeout(() => setSuccess(null), 3000);
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  }, [editReason, loadReasons]);

  const handleDelete = useCallback(async (id) => {
    setSaving(true);
    try {
      await deletePauseReason(id);
      setSuccess('Motivo de pausa removido.');
      setDeleteConfirm(null);
      loadReasons();
      setTimeout(() => setSuccess(null), 3000);
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  }, [loadReasons]);

  const globals = reasons.filter((r) => r.is_global !== false);
  const personals = reasons.filter((r) => r.is_global === false);

  return (
    <RequireRole roles={['manager', 'admin', 'root']}>
      <div className="ccv2-page-container ccv2-p5-container">
        <div className="ccv2-page-header">
          <div className="ccv2-page-title-area">
            <h1>Motivos de Pausa</h1>
            <p>Gerencie os motivos de pausa globais e personal do tenant</p>
          </div>
          <div className="ccv2-page-actions">
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.85rem', color: '#64748b' }}>
              <input type="checkbox" checked={includeInactive} onChange={(e) => setIncludeInactive(e.target.checked)} />
              Incluir inativos
            </label>
            <button className="ccv2-p5-btn ccv2-p5-btn-secondary" onClick={loadReasons}>🔄</button>
            <button className="ccv2-p5-btn ccv2-p5-btn-primary" onClick={() => { setEditReason(null); setShowModal(true); }}>
              + Novo Motivo
            </button>
          </div>
        </div>

        {success && <div className="ccv2-p5-success">{success} <button onClick={() => setSuccess(null)} style={{ marginLeft: 'auto', background: 'none', border: 'none', cursor: 'pointer' }}>×</button></div>}
        {error && <div className="ccv2-p5-error">⚠️ {error} <button onClick={() => setError(null)} style={{ marginLeft: 'auto', background: 'none', border: 'none', cursor: 'pointer' }}>×</button></div>}

        {loading ? (
          <div className="ccv2-config-loading"><div className="ccv2-spinner" /></div>
        ) : (
          <>
            {/* Global Reasons */}
            <div className="ccv2-p5-section">
              <div className="ccv2-p5-section-title">🌐 Motivos Globais ({globals.length})</div>
              <div className="ccv2-config-card">
                <div className="ccv2-config-table-wrapper">
                  <table className="ccv2-config-table">
                    <thead>
                      <tr>
                        <th>Cor</th>
                        <th>Nome</th>
                        <th>Tipo</th>
                        <th>Status</th>
                        <th>Ações</th>
                      </tr>
                    </thead>
                    <tbody>
                      {globals.length === 0 ? (
                        <tr><td colSpan={5} className="ccv2-p5-empty">Nenhum motivo global.</td></tr>
                      ) : globals.map((r) => (
                        <tr key={r.id_pause_reason}>
                          <td><span style={{ display: 'inline-block', width: 20, height: 20, borderRadius: 4, background: r.color || '#ccc', verticalAlign: 'middle' }} /></td>
                          <td className="ccv2-cell-name">{r.name}</td>
                          <td><span className="ccv2-p5-badge ccv2-p5-badge-global">Global</span></td>
                          <td><span className={`ccv2-p5-badge ${r.is_active !== false ? 'ccv2-p5-badge-active' : 'ccv2-p5-badge-inactive'}`}>{r.is_active !== false ? 'Ativo' : 'Inativo'}</span></td>
                          <td>
                            <div style={{ display: 'flex', gap: '0.4rem' }}>
                              <button className="ccv2-p5-btn ccv2-p5-btn-warning" style={{ fontSize: '0.75rem', padding: '0.3rem 0.6rem' }}
                                onClick={() => { setEditReason(r); setShowModal(true); }}>✏️</button>
                              <button className="ccv2-p5-btn ccv2-p5-btn-danger" style={{ fontSize: '0.75rem', padding: '0.3rem 0.6rem' }}
                                onClick={() => setDeleteConfirm(r)}>🗑️</button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            {/* Personal Reasons (read-only for manager) */}
            <div className="ccv2-p5-section">
              <div className="ccv2-p5-section-title">👤 Motivos Personal do Tenant ({personals.length})</div>
              <div className="ccv2-config-card">
                <div className="ccv2-config-table-wrapper">
                  <table className="ccv2-config-table">
                    <thead>
                      <tr>
                        <th>Nome</th>
                        <th>Tipo</th>
                        <th>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {personals.length === 0 ? (
                        <tr><td colSpan={3} className="ccv2-p5-empty">Nenhum motivo personal.</td></tr>
                      ) : personals.map((r) => (
                        <tr key={r.id_pause_reason}>
                          <td className="ccv2-cell-name">{r.name}</td>
                          <td><span className="ccv2-p5-badge ccv2-p5-badge-personal">Personal</span></td>
                          <td><span className={`ccv2-p5-badge ${r.is_active !== false ? 'ccv2-p5-badge-active' : 'ccv2-p5-badge-inactive'}`}>{r.is_active !== false ? 'Ativo' : 'Inativo'}</span></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </>
        )}

        {/* Create/Edit Modal */}
        {showModal && (
          <PauseReasonModal
            reason={editReason}
            onClose={() => { setShowModal(false); setEditReason(null); }}
            onSave={handleSave}
            saving={saving}
          />
        )}

        {/* Delete confirm modal */}
        {deleteConfirm && (
          <div className="ccv2-p5-modal-overlay" onClick={() => setDeleteConfirm(null)}>
            <div className="ccv2-p5-modal" onClick={(e) => e.stopPropagation()}>
              <div className="ccv2-p5-modal-header" style={{ background: 'linear-gradient(135deg, #e74c3c, #c0392b)', borderRadius: '14px 14px 0 0' }}>
                <h3 className="ccv2-p5-modal-title" style={{ color: '#fff' }}>Confirmar Exclusão</h3>
                <button className="ccv2-p5-modal-close" style={{ color: '#fff' }} onClick={() => setDeleteConfirm(null)}>×</button>
              </div>
              <div className="ccv2-p5-modal-body">
                <p>Tem certeza que deseja excluir o motivo <strong>{deleteConfirm.name}</strong>?</p>
                <div style={{ background: '#fff3cd', borderLeft: '4px solid #f39c12', padding: '0.75rem', borderRadius: 4, marginTop: '0.75rem', fontSize: '0.875rem' }}>
                  Esta ação é irreversível. Se houver histórico de pausas com este motivo, a exclusão pode falhar.
                </div>
              </div>
              <div className="ccv2-p5-modal-footer">
                <button className="ccv2-p5-btn ccv2-p5-btn-secondary" onClick={() => setDeleteConfirm(null)}>Cancelar</button>
                <button className="ccv2-p5-btn ccv2-p5-btn-danger" disabled={saving} onClick={() => handleDelete(deleteConfirm.id_pause_reason)}>
                  {saving ? '⌛' : '🗑️'} Excluir
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </RequireRole>
  );
}

export default PauseReasons;
